import React, { useState, useRef } from "react";
import axios from "axios";
import Recorder from "recorder-js";
import { FaMicrophone, FaStop, FaPaperPlane, FaSave, FaUpload } from "react-icons/fa";
import Navbar from "./Navbar";
import { useLocation ,useNavigate} from "react-router-dom";

const DoctorVoicePrescription = () => {
  const location = useLocation();

  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [prescriptionText, setPrescriptionText] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState([]);
  const [selectedLang, setSelectedLang] = useState("en");

  const recorderRef = useRef(null);
  const audioContextRef = useRef(null);
   const navigate = useNavigate();

  const languages = {
    en: "English",
    hi: "Hindi",
    mr: "Marathi",
    te: "Telugu",
    ta: "Tamil",
    bn: "Bengali",
    gu: "Gujarati",
    pa: "Punjabi"
  };

  // 🎙️ Start recording
  const startRecording = async () => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    recorderRef.current = new Recorder(audioContextRef.current);

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    await recorderRef.current.init(stream);
    recorderRef.current.start();

    setIsRecording(true);
  };

  // 🎙️ Stop recording
  const stopRecording = async () => {
    const { blob } = await recorderRef.current.stop();

    const url = URL.createObjectURL(blob);
    setAudioUrl(url);

    setIsRecording(false);
  };

  // 🚀 Send audio for transcription
  const handleSendAudio = async () => {
    if (!audioUrl) return;

    setLoading(true);

    const audioBlob = await fetch(audioUrl).then((res) => res.blob());

    const formData = new FormData();
    formData.append("file", audioBlob, "doctor_voice_note.wav");
    formData.append("lang", selectedLang); // ✅ send language code

    try {
      const response = await axios.post(
        "https://www.careplus.gopillz.com/voice_to_text",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setPrescriptionText(response.data.text);

    } catch (error) {
      console.error(error);
      alert("Transcription failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // 📂 Select file
  const handleFileChange = (e) => {
  const files = Array.from(e.target.files);

  if (files.length === 0) return;

  // ✅ Validate file types
  const validFiles = files.filter(file =>
    file.type.includes("pdf") || file.type.includes("image")
  );

  setUploadedFile(validFiles);
};

  // 📂 Upload prescription
 const handleUploadPrescription = async () => {

  // =========================
  // 📂 VALIDATION
  // =========================
  if (uploadedFile.length === 0) {
    alert("Please select at least one file (image or PDF).");
    return;
  }

  // 🔥 Prevent error before API call
  if (uploadedFile.length > 3) {
    alert(
      "❌ You selected too many files.\n\n" +
      "👉 Maximum allowed: 5 files at a time\n\n" +
      "✔️ Please remove some files and try again."
    );
    return;
  }

  setUploading(true);

  const formData = new FormData();

  uploadedFile.forEach(file => {
    formData.append("files", file);
  });

  formData.append("lang", selectedLang);

  try {
    const response = await axios.post(
      "https://lovencare.gopillz.com/api/doctor_prescription_analyze",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" }
      }
    );

    // =========================
    // ✅ SUCCESS
    // =========================
    setPrescriptionText(response.data.explanation || "No data returned");

    // ⚠️ Optional warning from backend
    if (response.data.warning) {
      alert(`⚠️ ${response.data.warning}`);
    } else {
      
      alert("✅ Prescription processed successfully!");
    }

  } catch (error) {

    console.log("FULL ERROR:", error);

    const status = error?.response?.status;
    const data = error?.response?.data;

    const backendError = data?.error || "Upload failed";
    const backendDetails = data?.details || "";

    const errorString = JSON.stringify(data || "");

    // =========================
    // 🔒 TOKEN EXPIRED
    // =========================
    if (status === 401) {
      alert("Session expired. Please login again.");
      navigate("/login");
      return;
    }

    // =========================
    // ❌ TOO MANY IMAGES (MODEL LIMIT)
    // =========================
    if (
      status === 400 &&
      errorString.includes("Too many images")
    ) {
      alert(
        "❌ Too many images/pages uploaded.\n\n" +
        "👉 AI supports only 5 images at a time.\n\n" +
        "✔️ Please upload fewer files or split your PDF."
      );
      return;
    }

    // =========================
    // 📁 FILE TOO LARGE
    // =========================
    if (status === 413) {
      alert("❌ File too large.\nPlease upload a smaller PDF/image.");
      return;
    }

    // =========================
    // 🚫 INVALID FILE TYPE
    // =========================
    if (status === 415) {
      alert("❌ Unsupported file type.\nPlease upload PDF or image.");
      return;
    }

    // =========================
    // ⚠️ AI SERVICE FAILED
    // =========================
    if (status === 500) {
      alert(
        "⚠️ AI processing failed.\n\n" +
        (backendDetails || "Please try again later.")
      );
      return;
    }

    // =========================
    // 🌐 NETWORK ERROR
    // =========================
    if (!error.response) {
      alert("🌐 Network error.\nPlease check your internet connection.");
      return;
    }

    // =========================
    // ❌ DEFAULT BACKEND ERROR
    // =========================
    alert(
      `❌ ${backendError}\n\n${backendDetails}`
    );

  } finally {
    setUploading(false);
  }
};

  // ✅ Submit prescription
  // const handleSubmitPrescription = async () => {

  //   if (!prescriptionText.trim()) {
  //     alert("Prescription text cannot be empty.");
  //     return;
  //   }

  //   const patient_id = location.state?.patient_id;

  //   const headersList = {
  //     Accept: "*/*",
  //     Authorization: `Bearer ${localStorage.getItem("token")}`,
  //     "Content-Type": "application/json",
  //   };

  //   setSubmitting(true);

  //   try {

  //     await axios.post(
  //       "https://www.careplus.gopillz.com/speak_generate_prescription",
  //       {
  //         doctor_note: prescriptionText,
  //         id: patient_id,
  //         lang: selectedLang // ✅ send language code
  //       },
  //       { headers: headersList }
  //     );

  //     alert("Prescription submitted successfully!");

  //     setPrescriptionText("");
  //     setAudioUrl("");

  //   } catch (error) {

  //     console.error(error);
  //     alert("Failed to submit prescription.");

  //   } finally {
  //     setSubmitting(false);
  //   }
  // };
//   const handleSubmitPrescription = async () => {

//   if (!prescriptionText.trim()) {
//     alert("Prescription text cannot be empty.");
//     return;
//   }

//   const patient_id = location.state?.patient_id;

//   const headersList = {
//     Accept: "*/*",
//     Authorization: `Bearer ${localStorage.getItem("token")}`,
//     "Content-Type": "application/json",
//   };

//   setSubmitting(true);

//   try {

//     await axios.post(
//       "https://www.careplus.gopillz.com/speak_generate_prescription",
//       {
//         doctor_note: prescriptionText,
//         id: patient_id,
//         lang: selectedLang
//       },
//       { headers: headersList }
//     );

//     alert("Prescription submitted successfully!");

//     setPrescriptionText("");
//     setAudioUrl("");

//   } catch (error) {

//     console.log("FULL ERROR:", error);
//     console.log("STATUS:", error?.response?.status);
//     console.log("DATA:", error?.response?.data);

//     // =========================
//     // 🚫 FREE LIMIT EXHAUSTED
//     // =========================
//     if (
//       error?.response?.status === 402 ||
//       error?.response?.data?.message?.toLowerCase().includes("limit")
//     ) {
//       alert("⚠️ Free limit exhausted. Please upgrade your plan.");
//       navigate("/subscription");   // ✅ React Router
//       return;
//     }

//     // =========================
//     // 🔒 TOKEN EXPIRED
//     // =========================
//     if (error?.response?.status === 401) {
//       alert("Session expired. Please login again.");
//       navigate("/login");
//       return;
//     }

//     // =========================
//     // ❌ OTHER ERRORS
//     // =========================
//     console.error(error);
//     alert("Failed to submit prescription.");

//   } finally {
//     setSubmitting(false);
//   }
// };


const handleSubmitPrescription = async () => {

  if (!prescriptionText.trim()) {
    alert("Prescription text cannot be empty.");
    return;
  }

  const patient_id = location.state?.patient_id;

  // ✅ Check patient ID
  if (!patient_id) {
    alert("Patient not found. Please go back and select a patient.");
    navigate("/dashboard");
    return;
  }

  // ✅ Check token
  const token = localStorage.getItem("token");

  if (!token) {
    alert("Please login again.");
    navigate("/login");
    return;
  }

  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  setSubmitting(true);

  try {

    const res = await axios.post(
      "https://www.careplus.gopillz.com/speak_generate_prescription",
      {
        doctor_note: prescriptionText,
        id: patient_id,
        lang: selectedLang
      },
      { headers }
    );

    alert("✅ Prescription submitted successfully!");

    setPrescriptionText("");
    setAudioUrl("");

  } 
  catch (error) {

  console.log("FULL ERROR:", error);

  const status = error?.response?.status;
  const data = error?.response?.data;

  // ✅ Backend error message
  const backendError = data?.error || "Something went wrong";
  const backendDetails = data?.details || "";

  // =========================
  // 🔒 TOKEN EXPIRED
  // =========================
  if (status === 401) {
    alert("Session expired. Please login again.");
    navigate("/login");
    return;
  }

  // =========================
  // 🚫 FREE LIMIT
  // =========================
  if (status === 402) {
    alert("⚠️ Free limit exhausted. Please upgrade.");
    navigate("/subscription");
    return;
  }

  // =========================
  // ❌ SHOW BACKEND ERROR
  // =========================
  alert(`${backendError}\n${backendDetails}`);

}
  
  finally {
    setSubmitting(false);
  }
};

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="min-h-screen flex flex-col items-center bg-gradient-to-br from-emerald-50 to-blue-100 p-6 mt-20">

        <h1 className="text-3xl font-bold mb-6 text-blue-800">
          🎙️ Upload Prescription/ Discharge Summary
        </h1>

        <div className="flex flex-col items-center space-y-6 bg-white p-8 rounded-2xl shadow-2xl max-w-lg w-full">

          {/* 🌐 Language selection */}

          <div className="w-full flex flex-col space-y-2">

            <label className="text-gray-700 font-semibold">
              🌐 Choose Language:
            </label>

            <select
              value={selectedLang}
              onChange={(e) => setSelectedLang(e.target.value)}
              className="border border-gray-300 rounded-lg p-2 w-full"
            >
              {Object.entries(languages).map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>

            <p className="text-sm text-gray-600 mt-1">
              Reason: The prescription will be sent to the patient in
              <span className="font-semibold"> {languages[selectedLang]}</span> via a voice note.
            </p>

          </div>

          {/* 🎙️ Recording */}

          {isRecording ? (
            <button
              onClick={stopRecording}
              className="flex items-center gap-2 px-6 py-3 rounded-full text-white bg-red-600 hover:bg-red-700 shadow-md"
            >
              <FaStop /> Stop Recording
            </button>
          ) : (
            <button
              onClick={startRecording}
              className="flex items-center gap-2 px-6 py-3 rounded-full text-white bg-green-600 hover:bg-green-700 shadow-md"
            >
              <FaMicrophone /> Start Recording
            </button>
          )}

          {/* 🎧 Audio playback */}

          {audioUrl && (
            <div className="flex flex-col items-center w-full space-y-4">

              <audio controls src={audioUrl} className="w-full rounded" />

              <button
                onClick={handleSendAudio}
                disabled={loading}
                className={`flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white w-full ${
                  loading ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                <FaPaperPlane />
                {loading ? "Transcribing..." : "Transcribe Audio"}
              </button>

            </div>
          )}

          {/* 📂 Upload prescription */}

          <div className="w-full flex flex-col space-y-3">

            <label className="text-gray-700 font-semibold">
              📂 Upload File:
            </label>

            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.wav,.mp3"
              multiple
              onChange={handleFileChange}
              className="border border-gray-300 rounded-lg p-2"
            />

            <button
              onClick={handleUploadPrescription}
              disabled={uploading}
              className={`flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white ${
                uploading ? "bg-gray-400" : "bg-indigo-600 hover:bg-indigo-700"
              }`}
            >
              <FaUpload />
              {uploading ? "Uploading..." : "Upload"}
            </button>

          </div>

          {/* ✍️ Prescription editor */}

          <div className="w-full">

            <label className="text-gray-700 font-semibold mb-2">
              ✍️ Write/Edit Prescription:
            </label>

            <textarea
              rows="6"
              placeholder="Write the prescription here..."
              value={prescriptionText}
              onChange={(e) => setPrescriptionText(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-4 focus:ring-2 focus:ring-blue-400 resize-none"
            />

          </div>

          {/* ✅ Submit */}

          <button
            onClick={handleSubmitPrescription}
            disabled={submitting}
            className={`flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white w-full ${
              submitting ? "bg-gray-400" : "bg-purple-600 hover:bg-purple-700"
            }`}
          >
            <FaSave />
            {submitting ? "Submitting..." : "Submit"}
          </button>

        </div>

      </div>
    </div>
  );
};

export default DoctorVoicePrescription;